import { useMutation } from '@tanstack/react-query'
import { KeyRound, Unplug } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { toast } from 'sonner'
import { trpc, type RouterOutputs } from 'shared/api'
import { Hint, SectionTitle } from 'shared/components/section'
import { Button } from 'shared/components/ui/button'
import { Input } from 'shared/components/ui/input'
import { useTranslate } from 'shared/lib/i18n'
import { formatDateTime } from 'shared/lib/utils/date'

type Partner = RouterOutputs['partners']['List'][number]

/** What the admin is shown once, at the moment a B2B key is issued. */
export interface IssuedB2bKey {
	keyId: string
	secret: string
}

type Props = {
	partner: Partner
	onB2bKeyIssued: (key: IssuedB2bKey) => void
}

/**
 * The partner's Connection by API (ADR-0040, CONTEXT — Connection): the B2B key TezGo
 * signs every fact with, and the webhook the facts go to. The secret is shown once,
 * at issue; the panel shows the key's id and its last four signs. Without a webhook the partner's
 * facts are not sent — they stay on the Event Log. The server decides what is
 * accepted (a webhook only over a key); this card renders its answers.
 */
export function PartnerConnection({ partner, onB2bKeyIssued }: Props) {
	const { t } = useTranslate()
	const { webhookUrl, b2bKey } = partner.connection

	const issueKey = useMutation({
		...trpc.partners.IssueB2bKey.mutationOptions(),
		onSuccess: (data) => onB2bKeyIssued({ keyId: data.keyId, secret: data.secret }),
		onError: (error) => toast.error(error.message),
	})

	return (
		<section className='flex flex-col gap-3 px-4' data-testid='partner-connection'>
			<SectionTitle title={t('partners.connectionTitle')} hint={t('partners.connectionHint')} />

			<div className='flex flex-wrap items-center justify-between gap-2'>
				<div className='flex flex-col gap-0.5 text-sm'>
					<div className='flex items-center gap-1.5'>
						<span className='font-medium'>{t('partners.b2bKeyLabel')}</span>
						<Hint>{t('partners.b2bKeyHint')}</Hint>
					</div>
					<div data-testid='partner-b2b-key'>
						{b2bKey ? (
							<>
								<span className='font-mono'>{b2bKey.keyId}</span>{' '}
								<span className='font-mono text-muted-foreground'>{b2bKey.preview}</span>{' '}
								<span className='text-muted-foreground'>
									{t('partners.b2bKeyIssuedAt', { at: formatDateTime(b2bKey.issuedAt) })}
								</span>
							</>
						) : (
							<span className='text-muted-foreground'>{t('partners.b2bKeyNone')}</span>
						)}
					</div>
				</div>
				<Button
					variant='outline'
					size='sm'
					data-testid='partner-b2b-issue'
					disabled={issueKey.isPending}
					onClick={() => issueKey.mutate({ partnerId: partner._id })}
				>
					<KeyRound />
					{b2bKey ? t('partners.b2bKeyReplace') : t('partners.b2bKeyIssue')}
				</Button>
			</div>

			{/* Keyed by the saved address, so the field starts from it again once it changes. */}
			<WebhookForm
				key={webhookUrl ?? ''}
				partnerId={partner._id}
				webhookUrl={webhookUrl}
				hasKey={Boolean(b2bKey)}
			/>
		</section>
	)
}

/** The webhook address: saved, or cleared — then the partner's facts stay on the Event Log. */
function WebhookForm({
	partnerId,
	webhookUrl,
	hasKey,
}: {
	partnerId: string
	webhookUrl: string | null
	hasKey: boolean
}) {
	const { t } = useTranslate()
	const [url, setUrl] = useState(webhookUrl ?? '')

	const setWebhook = useMutation({
		...trpc.partners.SetWebhook.mutationOptions(),
		onSuccess: (data) =>
			toast.success(
				data.connection.webhookUrl ? t('partners.webhookSaved') : t('partners.webhookCleared'),
			),
		onError: (error) => toast.error(error.message),
	})

	const save = (event: FormEvent) => {
		event.preventDefault()
		setWebhook.mutate({ partnerId, webhookUrl: url.trim() })
	}

	return (
		<form className='grid gap-2' onSubmit={save}>
			<label className='text-sm font-medium' htmlFor={`webhook-${partnerId}`}>
				{t('partners.webhookLabel')}
			</label>
			<div className='flex flex-wrap gap-2'>
				<Input
					id={`webhook-${partnerId}`}
					data-testid='partner-webhook'
					className='min-w-64 flex-1 font-mono text-xs'
					value={url}
					onChange={(event) => setUrl(event.target.value)}
					placeholder={t('partners.webhookPlaceholder')}
					disabled={!hasKey}
				/>
				<Button
					data-testid='partner-webhook-save'
					disabled={
						!hasKey || setWebhook.isPending || !url.trim() || url.trim() === webhookUrl
					}
				>
					{t('partners.webhookSave')}
				</Button>
				{webhookUrl && (
					<Button
						type='button'
						variant='ghost'
						size='sm'
						data-testid='partner-webhook-clear'
						disabled={setWebhook.isPending}
						onClick={() => setWebhook.mutate({ partnerId, webhookUrl: null })}
					>
						<Unplug />
						{t('partners.webhookClear')}
					</Button>
				)}
			</div>
			{!webhookUrl && (
				<div className='text-sm text-muted-foreground' data-testid='partner-webhook-none'>
					{hasKey ? t('partners.webhookNone') : t('partners.webhookNeedsKey')}
				</div>
			)}
		</form>
	)
}
